import React, { useState } from "react";
import { Plus, Trash2, ChevronDown, AlertTriangle, ExternalLink, Target } from "lucide-react";

export function SessionEditor({ 
  sessions, 
  programIndex = 0, 
  isEditing, 
  onUpdate, 
  onAdd, 
  onRemove, 
  duplicateWarnings = [],
  variant = "default" // "default" or "compact"
}) {
  const [expandedSessions, setExpandedSessions] = useState({});

  const toggleSession = (idx) => {
    setExpandedSessions(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const validateSession = (session) => {
    const errors = [];
    if (!session.startDate) errors.push("Start date required");
    if (!session.endDate) errors.push("End date required");
    if (session.startDate && session.endDate) {
      const start = new Date(session.startDate);
      const end = new Date(session.endDate);
      if (end < start) errors.push("End date before start");
    }
    return errors;
  };

  if (!sessions || sessions.length === 0) {
    return (
      <div className="mt-2">
        <div className="text-gray-500 text-xs mb-2">No sessions found</div>
        {isEditing && (
          <button
            type="button"
            onClick={() => onAdd(programIndex)}
            className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
          >
            <Plus className="w-3 h-3" />
            Add Session
          </button>
        )}
      </div>
    );
  }

  const isCompact = variant === "compact";

  return (
    <div className={`${isCompact ? "mt-2" : "mt-3 space-y-2"}`}>
      <div className="flex items-center justify-between">
        <span className={`text-gray-400 ${isCompact ? "text-xs" : ""}`}>
          Sessions ({sessions.length})
          {sessions.some(s => validateSession(s).length > 0) && (
            <span className="ml-2 text-orange-400">⚠️ Needs attention</span>
          )}
        </span>
        {isEditing && (
          <button
            type="button"
            onClick={() => onAdd(programIndex)}
            className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
          >
            <Plus className="w-3 h-3" />
            Add Session
          </button>
        )}
      </div>
      
      {sessions.map((session, sessionIdx) => {
        const errors = validateSession(session);
        const isExpanded = expandedSessions[sessionIdx] || isEditing;
        
        // Check for duplicate warnings for this session
        const dupWarning = duplicateWarnings?.find(
          d => d.programName === session.programName && d.sessionName === session.name
        );
        
        return (
          <div 
            key={`session-${sessionIdx}`}
            className={`bg-gray-700/30 rounded p-2 ${errors.length > 0 ? "border border-orange-500/30" : ""} ${dupWarning?.isDuplicate ? "border border-red-500/30" : dupWarning ? "border border-yellow-500/30" : ""}`}
          >
            {/* Session Header */}
            <div 
              className="flex items-center justify-between cursor-pointer"
              onClick={() => !isEditing && toggleSession(sessionIdx)}
            >
              <div className="flex items-center gap-2">
                <span className="text-white text-sm">
                  {session.name || `Session ${sessionIdx + 1}`}
                </span>
                {errors.length > 0 && (
                  <span className="text-orange-400 text-xs" title={errors.join(", ")}>
                    <AlertTriangle className="w-3 h-3 inline" />
                  </span>
                )}
                {dupWarning && (
                  <span 
                    className={`text-xs px-1.5 py-0.5 rounded ${dupWarning.isDuplicate ? "bg-red-500/20 text-red-400" : "bg-yellow-500/20 text-yellow-400"}`}
                    title={dupWarning.matchDetails.join(", ")}
                  >
                    {dupWarning.isDuplicate ? "Duplicate" : `${dupWarning.confidence}% match`}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-xs">
                  {session.startDate || session.endDate ? (
                    <>
                      {session.startDate && new Date(session.startDate).toLocaleDateString()}
                      {session.startDate && session.endDate && " - "}
                      {session.endDate && new Date(session.endDate).toLocaleDateString()}
                    </>
                  ) : (
                    <span className="text-orange-400">No dates</span>
                  )}
                </span>
                {!isEditing && (
                  <ChevronDown 
                    className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`} 
                  />
                )}
                {isEditing && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(programIndex, sessionIdx);
                    }}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>

            {/* Session Details */}
            {(isExpanded || isEditing) && (
              <div className="mt-2 pt-2 border-t border-gray-600/30 space-y-2">
                {isEditing ? (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={session.name || ""}
                        onChange={(e) => onUpdate(programIndex, sessionIdx, "name", e.target.value)}
                        placeholder="Session name"
                        className="px-2 py-1 bg-gray-600 rounded text-white text-xs"
                      />
                      <input
                        type="text"
                        value={session.daysOfWeek || ""}
                        onChange={(e) => onUpdate(programIndex, sessionIdx, "daysOfWeek", e.target.value)}
                        placeholder="Days (e.g., Mon/Wed)"
                        className="px-2 py-1 bg-gray-600 rounded text-white text-xs"
                      />
                    </div>
                    <div>
                      <label htmlFor={`session-${programIndex}-${sessionIdx}-description`} className="text-xs text-gray-400 block mb-1">Description (optional)</label>
                      <textarea
                        id={`session-${programIndex}-${sessionIdx}-description`}
                        value={session.description || ""}
                        onChange={(e) => onUpdate(programIndex, sessionIdx, "description", e.target.value)}
                        placeholder="Session description..."
                        rows={2}
                        className="w-full px-2 py-1 bg-gray-600 rounded text-white text-xs resize-none"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label htmlFor={`session-${programIndex}-${sessionIdx}-startDate`} className="text-xs text-gray-400 block mb-1">Start Date *</label>
                        <input
                          id={`session-${programIndex}-${sessionIdx}-startDate`}
                          type="date"
                          value={session.startDate || ""}
                          onChange={(e) => onUpdate(programIndex, sessionIdx, "startDate", e.target.value)}
                          className={`w-full px-2 py-1 bg-gray-600 rounded text-white text-xs ${!session.startDate ? "border border-orange-500" : ""}`}
                        />
                      </div>
                      <div>
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label htmlFor={`session-${programIndex}-${sessionIdx}-endDate`} className="text-xs text-gray-400 block mb-1">End Date *</label>
                        <input
                          id={`session-${programIndex}-${sessionIdx}-endDate`}
                          type="date"
                          value={session.endDate || ""}
                          onChange={(e) => onUpdate(programIndex, sessionIdx, "endDate", e.target.value)}
                          className={`w-full px-2 py-1 bg-gray-600 rounded text-white text-xs ${!session.endDate ? "border border-orange-500" : ""}`}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label htmlFor={`session-${programIndex}-${sessionIdx}-startTime`} className="text-xs text-gray-400 block mb-1">Start Time</label>
                        <input
                          id={`session-${programIndex}-${sessionIdx}-startTime`}
                          type="time"
                          value={session.startTime || ""}
                          onChange={(e) => onUpdate(programIndex, sessionIdx, "startTime", e.target.value)}
                          className="w-full px-2 py-1 bg-gray-600 rounded text-white text-xs"
                        />
                      </div>
                      <div>
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label htmlFor={`session-${programIndex}-${sessionIdx}-endTime`} className="text-xs text-gray-400 block mb-1">End Time</label>
                        <input
                          id={`session-${programIndex}-${sessionIdx}-endTime`}
                          type="time"
                          value={session.endTime || ""}
                          onChange={(e) => onUpdate(programIndex, sessionIdx, "endTime", e.target.value)}
                          className="w-full px-2 py-1 bg-gray-600 rounded text-white text-xs"
                        />
                      </div>
                      <div>
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label htmlFor={`session-${programIndex}-${sessionIdx}-capacity`} className="text-xs text-gray-400 block mb-1">Capacity</label>
                        <input
                          id={`session-${programIndex}-${sessionIdx}-capacity`}
                          type="number"
                          value={session.capacity || ""}
                          onChange={(e) => onUpdate(programIndex, sessionIdx, "capacity", Number.parseInt(e.target.value, 10) || undefined)}
                          placeholder="20"
                          className="w-full px-2 py-1 bg-gray-600 rounded text-white text-xs"
                        />
                      </div>
                    </div>
                    <div>
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor={`session-${programIndex}-${sessionIdx}-price`} className="text-xs text-gray-400 block mb-1">Price Total ($)</label>
                      <input
                        id={`session-${programIndex}-${sessionIdx}-price`}
                        type="number"
                        step="0.01"
                        value={session.priceTotal || ""}
                        onChange={(e) => onUpdate(programIndex, sessionIdx, "priceTotal", Number.parseFloat(e.target.value) || undefined)}
                        placeholder="150.00"
                        className="w-full px-2 py-1 bg-gray-600 rounded text-white text-xs"
                      />
                    </div>
                    <div>
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor={`session-${programIndex}-${sessionIdx}-registrationUrl`} className="text-xs text-gray-400 block mb-1">Registration URL</label>
                      <input
                        id={`session-${programIndex}-${sessionIdx}-registrationUrl`}
                        type="url"
                        value={session.registrationUrl || ""}
                        onChange={(e) => onUpdate(programIndex, sessionIdx, "registrationUrl", e.target.value)}
                        placeholder="https://..."
                        className="w-full px-2 py-1 bg-gray-600 rounded text-white text-xs"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        id={`session-${programIndex}-${sessionIdx}-isTryout`}
                        type="checkbox"
                        checked={session.isTryout || false}
                        onChange={(e) => onUpdate(programIndex, sessionIdx, "isTryout", e.target.checked)}
                        className="w-4 h-4 rounded border-gray-600 bg-gray-600 text-indigo-600 focus:ring-indigo-500"
                      />
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor={`session-${programIndex}-${sessionIdx}-isTryout`} className="text-xs text-gray-400 flex items-center gap-1">
                        <Target className="w-3 h-3" />
                        Tryout Session
                      </label>
                    </div>
                  </>
                ) : (
                  <div className="text-xs text-gray-400 space-y-1">
                    {/* Show description */}
                    {session.description && (
                      <div className="text-gray-300 italic mb-2">{session.description}</div>
                    )}
                    {/* Always show dates if available */}
                    {(session.startDate || session.endDate) && (
                      <div className="text-gray-300">
                        <span className="text-gray-500">Dates:</span>{" "}
                        {session.startDate ? new Date(session.startDate).toLocaleDateString() : "?"}
                        {" - "}
                        {session.endDate ? new Date(session.endDate).toLocaleDateString() : "?"}
                      </div>
                    )}
                    {/* Show days of week */}
                    {session.daysOfWeek && (
                      <div><span className="text-gray-500">Days:</span> <span className="text-gray-300">{session.daysOfWeek}</span></div>
                    )}
                    {/* Show times */}
                    {(session.startTime || session.endTime) && (
                      <div><span className="text-gray-500">Time:</span> <span className="text-gray-300">
                        {session.startTime || "?"}{session.startTime && session.endTime && " - "}{session.endTime || "?"}
                      </span></div>
                    )}
                    {/* Show capacity */}
                    {session.capacity && (
                      <div><span className="text-gray-500">Capacity:</span> <span className="text-gray-300">{session.capacity}</span></div>
                    )}
                    {/* Show price */}
                    {session.priceTotal && (
                      <div><span className="text-gray-500">Price:</span> <span className="text-gray-300">${session.priceTotal}</span></div>
                    )}
                    {/* Show registration URL */}
                    {session.registrationUrl && (
                      <div className="flex items-center gap-1">
                        <span className="text-gray-500">Registration:</span>
                        <a
                          href={session.registrationUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-400 hover:text-indigo-300 text-xs flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Link
                        </a>
                      </div>
                    )}
                    {/* Show tryout badge */}
                    {session.isTryout && (
                      <div className="flex items-center gap-1">
                        <span className="text-orange-400 text-xs px-2 py-0.5 bg-orange-500/20 rounded flex items-center gap-1">
                          <Target className="w-3 h-3" />
                          Tryout Session
                        </span>
                      </div>
                    )}
                    {/* Show if data is minimal */}
                    {!session.startDate && !session.endDate && !session.daysOfWeek && !session.startTime && !session.capacity && !session.priceTotal && !session.registrationUrl && !session.isTryout && !session.description && (
                      <div className="text-orange-400 italic">Session name only - add dates/details in edit mode</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default SessionEditor;
